
import { Book, Briefcase, Users, GraduationCap, CheckCircle, Network, Target, FastForward } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const RNCP = () => {
  const { t } = useTranslation('rncp');

  const users = [
    {
      title: t('users.items.candidates.title'),
      items: t('users.items.candidates.description', { returnObjects: true }) as string[],
      icon: <Users className="w-6 h-6 text-brand-orange" />
    },
    {
      title: t('users.items.employers.title'),
      items: t('users.items.employers.description', { returnObjects: true }) as string[],
      icon: <Briefcase className="w-6 h-6 text-brand-orange" />
    },
    {
      title: t('users.items.training.title'),
      items: t('users.items.training.description', { returnObjects: true }) as string[],
      icon: <GraduationCap className="w-6 h-6 text-brand-orange" />
    },
    {
      title: t('users.items.vae.title'),
      items: t('users.items.vae.description', { returnObjects: true }) as string[],
      icon: <CheckCircle className="w-6 h-6 text-brand-orange" />
    }
  ];

  const usagesConcrets = [
    {
      title: t('usages.items.companies.title'),
      description: t('usages.items.companies.description'),
      icon: <Briefcase className="w-6 h-6 text-brand-orange" />
    },
    {
      title: t('usages.items.training.title'),
      description: t('usages.items.training.description'),
      icon: <GraduationCap className="w-6 h-6 text-brand-orange" />
    },
    {
      title: t('usages.items.candidates.title'),
      description: t('usages.items.candidates.description'),
      icon: <Target className="w-6 h-6 text-brand-orange" />
    }
  ];

  const innovations = t('innovation.benefits', { returnObjects: true }) as string[];

  return (
    <div className="bg-background">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(45rem_50rem_at_top,theme(colors.brand.orange/8),transparent)] dark:bg-[radial-gradient(45rem_50rem_at_top,theme(colors.brand.orange/4),transparent)]" />
          <div className="absolute inset-0 bg-[radial-gradient(45rem_50rem_at_top_right,theme(colors.brand.blue/5),transparent)] dark:bg-[radial-gradient(45rem_50rem_at_top_right,theme(colors.brand.blue/3),transparent)]" />
        </div>
        
        <div className="container-custom relative z-10 py-24">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-blue/10 dark:bg-brand-blue/5 text-brand-blue dark:text-brand-orange mb-8">
              <Book className="w-5 h-5" />
              <span className="text-sm font-medium">{t('badge')}</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-brand-blue to-brand-orange leading-[1.2] pb-4">
              {t('title')}
            </h1>
            
            <p className="text-xl text-muted-foreground mb-12 max-w-2xl mx-auto">
              {t('description')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/search" 
                className="inline-flex items-center px-6 py-3 rounded-lg bg-gradient-to-r from-brand-blue to-brand-orange text-white hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {t('cta.explore')}
              </a>
              <Link
                to="/contact" 
                className="inline-flex items-center px-6 py-3 rounded-lg border-2 border-brand-orange text-brand-orange hover:bg-brand-orange hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {t('cta.contact')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Users Section */}
      <section className="py-24">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-16 bg-clip-text text-transparent bg-gradient-to-r from-brand-blue to-brand-orange">
              {t('users.title')}
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-8">
              {users.map((user, index) => (
                <div 
                  key={index}
                  className="group bg-card rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-border hover:-translate-y-1"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="p-3 bg-brand-orange/10 dark:bg-brand-orange/5 rounded-xl group-hover:scale-110 transition-transform">
                      {user.icon}
                    </div>
                    <h3 className="text-xl font-medium text-brand-blue dark:text-brand-orange group-hover:text-[#8B5CF6] transition-colors">
                      {user.title}
                    </h3>
                  </div>
                  <ul className="space-y-3 pl-16">
                    {user.items.map((item: string, itemIndex: number) => (
                      <li key={itemIndex} className="text-muted-foreground flex items-start gap-2">
                        <span className="text-brand-orange">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Innovation Section */}
      <section className="py-24 bg-card">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-brand-blue to-brand-orange">
                {t('innovation.title')}
              </h2>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-orange/10 dark:bg-brand-orange/5 text-brand-orange mb-8">
                <Network className="w-5 h-5" />
                <span className="text-sm font-medium">{t('innovation.subtitle')}</span>
              </div>
              <p className="text-lg text-muted-foreground mb-12">
                {t('innovation.description')}
              </p>
            </div>

            <div className="bg-background rounded-xl p-8 shadow-lg mb-16 border border-border">
              <ul className="space-y-6">
                {innovations.map((innovation, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <FastForward className="w-6 h-6 text-brand-orange flex-shrink-0 mt-1" />
                    <p className="text-muted-foreground">{innovation}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Usage Concrets Section */}
      <section className="py-24">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-16 bg-clip-text text-transparent bg-gradient-to-r from-brand-blue to-brand-orange">
              {t('usages.title')}
            </h2>
            
            <div className="grid sm:grid-cols-3 gap-8">
              {usagesConcrets.map((usage, index) => (
                <div 
                  key={index}
                  className="group bg-card rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-border hover:-translate-y-1"
                >
                  <div className="flex items-center justify-center mb-6">
                    <div className="p-3 bg-brand-orange/10 dark:bg-brand-orange/5 rounded-xl group-hover:scale-110 transition-transform">
                      {usage.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-medium text-brand-blue dark:text-brand-orange mb-4 text-center group-hover:text-[#8B5CF6] transition-colors">
                    {usage.title}
                  </h3>
                  <p className="text-muted-foreground text-center">
                    {usage.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-16 text-center">
              <Link
                to="/search-engine"
                className="inline-flex items-center px-8 py-4 rounded-lg bg-gradient-to-r from-brand-blue to-brand-orange text-white hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl text-lg"
              >
                {t('cta.explore')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RNCP;
